import React, { useState, useEffect } from 'react';
import { Check, X, AlertCircle, Save, RefreshCw } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';

interface FinancialItem {
  label: string;
  amount: number;
}

interface ProfitLossData {
  date: string;
  month: string;
  year: number;
  total_income: number;
  total_cogs: number;
  gross_profit: number;
  total_expense: number;
  net_ordinary_income: number;
  financials: {
    Income: FinancialItem[];
    "Cost of Goods Sold": FinancialItem[];
    Expenses: FinancialItem[];
    "Other Income": FinancialItem[];
    "Other Expense": FinancialItem[];
  };
}

interface ProfitLossMappingInterfaceProps {
  extractedData: any;
  onSave: (mappedData: ProfitLossData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  dealId?: string;
  extractionId?: string;
}

export function ProfitLossMappingInterface({
  extractedData,
  onSave,
  onCancel,
  isLoading = false,
  dealId,
  extractionId
}: ProfitLossMappingInterfaceProps) {
  const { user } = useUser();
  const [mappedData, setMappedData] = useState<ProfitLossData | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    if (extractedData) {
      // Initialize mapped data from extracted data
      const initialData: ProfitLossData = {
        date: extractedData.date || new Date().toISOString().split('T')[0],
        month: extractedData.month || '',
        year: extractedData.year || new Date().getFullYear(),
        total_income: extractedData.total_income || 0,
        total_cogs: extractedData.total_cogs || 0,
        gross_profit: extractedData.gross_profit || 0,
        total_expense: extractedData.total_expense || 0,
        net_ordinary_income: extractedData.net_ordinary_income || 0,
        financials: {
          Income: extractedData.financials?.Income || [],
          "Cost of Goods Sold": extractedData.financials?.["Cost of Goods Sold"] || [],
          Expenses: extractedData.financials?.Expenses || [],
          "Other Income": extractedData.financials?.["Other Income"] || [],
          "Other Expense": extractedData.financials?.["Other Expense"] || []
        }
      };
      setMappedData(initialData);
      validateData(initialData);
    }
  }, [extractedData]);

  const validateData = (data: ProfitLossData) => {
    const errors: string[] = [];
    
    // Required field validation
    if (!data.date) errors.push("Date is required");
    if (!data.month) errors.push("Month is required");
    if (!data.year) errors.push("Year is required");
    
    // Date format validation
    if (data.date && !/^\d{4}-\d{2}-\d{2}$/.test(data.date)) {
      errors.push("Date must be in YYYY-MM-DD format");
    }
    
    // Year validation
    if (data.year && (data.year < 1900 || data.year > 2100)) {
      errors.push("Year must be between 1900 and 2100");
    }
    
    // Financial validation
    if (data.total_income < 0) errors.push("Total income cannot be negative");
    if (data.total_cogs < 0) errors.push("Cost of goods sold cannot be negative");
    if (data.total_expense < 0) errors.push("Total expenses cannot be negative");
    
    // Mathematical consistency
    const calculatedGrossProfit = data.total_income - data.total_cogs;
    if (Math.abs(data.gross_profit - calculatedGrossProfit) > 0.01) {
      errors.push(`Gross profit calculation mismatch: Expected ${calculatedGrossProfit.toFixed(2)}, Found ${data.gross_profit.toFixed(2)}`);
    }
    
    // Validate financial categories
    const requiredCategories = ["Income", "Cost of Goods Sold", "Expenses", "Other Income", "Other Expense"];
    for (const category of requiredCategories) {
      if (!data.financials[category] || !Array.isArray(data.financials[category])) {
        errors.push(`${category} must be an array`);
        continue;
      }
      
      for (let i = 0; i < data.financials[category].length; i++) {
        const item = data.financials[category][i];
        if (!item.label || item.label.trim() === '') {
          errors.push(`${category} item ${i + 1} must have a label`);
        }
        if (typeof item.amount !== 'number' || isNaN(item.amount)) {
          errors.push(`${category} item ${i + 1} must have a valid amount`);
        }
      }
    }
    
    setValidationErrors(errors);
    setIsValid(errors.length === 0);
  };

  const updateField = (field: keyof ProfitLossData, value: any) => {
    if (!mappedData) return;
    
    const updatedData = { ...mappedData, [field]: value };
    setMappedData(updatedData);
    validateData(updatedData);
  };

  const updateFinancialItem = (category: keyof ProfitLossData['financials'], index: number, field: keyof FinancialItem, value: any) => {
    if (!mappedData) return;
    
    const updatedFinancials = { ...mappedData.financials };
    const updatedCategory = [...updatedFinancials[category]];
    updatedCategory[index] = { ...updatedCategory[index], [field]: value };
    updatedFinancials[category] = updatedCategory;
    
    const updatedData = { ...mappedData, financials: updatedFinancials };
    setMappedData(updatedData);
    validateData(updatedData);
  };

  const addFinancialItem = (category: keyof ProfitLossData['financials']) => {
    if (!mappedData) return;
    
    const updatedFinancials = { ...mappedData.financials };
    updatedFinancials[category] = [...updatedFinancials[category], { label: '', amount: 0 }];
    
    const updatedData = { ...mappedData, financials: updatedFinancials };
    setMappedData(updatedData);
    validateData(updatedData);
  };

  const removeFinancialItem = (category: keyof ProfitLossData['financials'], index: number) => {
    if (!mappedData) return;
    
    const updatedFinancials = { ...mappedData.financials };
    updatedFinancials[category] = updatedFinancials[category].filter((_, i) => i !== index);
    
    const updatedData = { ...mappedData, financials: updatedFinancials };
    setMappedData(updatedData);
    validateData(updatedData);
  };

  const recalculateTotals = () => {
    if (!mappedData) return;
    
    const financials = mappedData.financials;
    
    // Calculate totals from line items
    const totalIncome = financials.Income.reduce((sum, item) => sum + (item.amount || 0), 0) +
                       financials["Other Income"].reduce((sum, item) => sum + (item.amount || 0), 0);
    
    const totalCogs = financials["Cost of Goods Sold"].reduce((sum, item) => sum + (item.amount || 0), 0);
    
    const totalExpense = financials.Expenses.reduce((sum, item) => sum + (item.amount || 0), 0) +
                        financials["Other Expense"].reduce((sum, item) => sum + (item.amount || 0), 0);
    
    const grossProfit = totalIncome - totalCogs;
    const netOrdinaryIncome = grossProfit - totalExpense;
    
    const updatedData = {
      ...mappedData,
      total_income: totalIncome,
      total_cogs: totalCogs,
      gross_profit: grossProfit,
      total_expense: totalExpense,
      net_ordinary_income: netOrdinaryIncome
    };
    
    setMappedData(updatedData);
    validateData(updatedData);
  };

  const handleSave = async () => {
    if (mappedData && isValid) {
      // First call the parent onSave to update the extraction
      onSave(mappedData);
      
      // Then save to the profit_loss_statements table if we have the required IDs
      // NOTE: Temporary persistence target
      // - This client request posts to `/save-profit-loss-data` which writes to the
      //   legacy table `profit_loss_statements`.
      // - As part of the migration, persistence will move to `pnl_data` and/or
      //   document-specific tables depending on document type.
      // - If `profit_loss_statements` is dropped during migration, the backend can
      //   safely no-op without breaking the UI (this component already handles
      //   backend failures without blocking the user flow).
      if (dealId && extractionId) {
        try {
          const baseUrl = (import.meta as any).env?.VITE_BACKEND_URL || '/api';
          const formData = new FormData();
          formData.append('extraction_id', extractionId);
          formData.append('deal_id', dealId);
          formData.append('user_id', user?.id || '');
          formData.append('profit_loss_data', JSON.stringify(mappedData));
          
          const response = await fetch(`${baseUrl}/save-profit-loss-data`, {
            method: 'POST',
            body: formData,
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            console.error('Failed to save to database:', errorData);
            // Don't throw error here as the extraction was already saved
          }
        } catch (error) {
          console.error('Error saving to database:', error);
          // Don't throw error here as the extraction was already saved
        }
      }
    }
  };

  if (!mappedData) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500">Loading mapping interface...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Profit & Loss Data Mapping</h2>
        <p className="text-gray-600">Review and correct the extracted financial data before saving.</p>
      </div>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center mb-2">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <h3 className="font-semibold text-red-800">Validation Errors</h3>
          </div>
          <ul className="list-disc list-inside text-red-700 space-y-1">
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Basic Information */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              value={mappedData.date}
              onChange={(e) => updateField('date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
            <input
              type="text"
              value={mappedData.month}
              onChange={(e) => updateField('month', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., January, Q1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <input
              type="number"
              value={mappedData.year}
              onChange={(e) => updateField('year', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="1900"
              max="2100"
            />
          </div>
        </div>
      </div>

      {/* Summary Totals */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Summary Totals</h3>
          <button
            onClick={recalculateTotals}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Recalculate
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total Income</label>
            <input
              type="number"
              value={mappedData.total_income}
              onChange={(e) => updateField('total_income', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total COGS</label>
            <input
              type="number"
              value={mappedData.total_cogs}
              onChange={(e) => updateField('total_cogs', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Gross Profit</label>
            <input
              type="number"
              value={mappedData.gross_profit}
              onChange={(e) => updateField('gross_profit', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total Expenses</label>
            <input
              type="number"
              value={mappedData.total_expense}
              onChange={(e) => updateField('total_expense', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Net Ordinary Income</label>
            <input
              type="number"
              value={mappedData.net_ordinary_income}
              onChange={(e) => updateField('net_ordinary_income', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              step="0.01"
            />
          </div>
        </div>
      </div>

      {/* Financial Categories */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Categories</h3>
        {Object.entries(mappedData.financials).map(([category, items]) => (
          <div key={category} className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-800">{category}</h4>
              <button
                onClick={() => addFinancialItem(category as keyof ProfitLossData['financials'])}
                className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                Add Item
              </button>
            </div>
            <div className="space-y-2">
              {items.map((item, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="text"
                    value={item.label}
                    onChange={(e) => updateFinancialItem(category as keyof ProfitLossData['financials'], index, 'label', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Item label"
                  />
                  <input
                    type="number"
                    value={item.amount}
                    onChange={(e) => updateFinancialItem(category as keyof ProfitLossData['financials'], index, 'amount', parseFloat(e.target.value) || 0)}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    step="0.01"
                  />
                  <button
                    onClick={() => removeFinancialItem(category as keyof ProfitLossData['financials'], index)}
                    className="p-2 text-red-600 hover:bg-red-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {items.length === 0 && (
                <div className="text-center py-4 text-gray-500">
                  No items in this category. Click "Add Item" to add one.
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
        <button
          onClick={onCancel}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={!isValid || isLoading}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isLoading ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save Mapped Data
        </button>
      </div>
    </div>
  );
}
